import {
    ChartSeries
} from "../../../features/portfolio-profile/components/charts/portfolio-balance-profile/types/ChartSeries";
import type { MarketDataDto } from "../../../shared/types/MarketDataDto";
import { NumberFormatOptions, numberFormatters } from "../../shared/utils/numberFormatters";
import {
    chartTooltipService
} from "../../../features/portfolio-profile/components/charts/portfolio-greek-profile/services/ChartTooltipService";


class ImpliedVolatilityTooltipService {
    getTooltipContent(
        chartData: number[][],
        seriesCollection: ChartSeries[],
        marketDataDto: MarketDataDto,
        selectedStrike: number,
    ): string {
        if (seriesCollection.length === 0)
            return "No one series isn't selected";
        
        const strikePriceRow = this.getStrikePriceRow(seriesCollection, marketDataDto.UnderlyingPrice!);
        const impliedVolatilityRow = this.getImpliedVolatilityRow(seriesCollection);
        const tooltipDivider = chartTooltipService.getTooltipDivider();
        const estimatedImpliedVolatilityRow = this.getEstimatedImpliedVolatilityRow(
            chartData, seriesCollection, marketDataDto.UnderlyingPrice!, selectedStrike);
        
        return strikePriceRow + impliedVolatilityRow + tooltipDivider + estimatedImpliedVolatilityRow;
    }

    private getStrikePriceRow(seriesCollection: ChartSeries[], underlyingPrice: number): string {
        const hoveredCurrentPrice = seriesCollection[0].value[0];
        const deltaPrice = hoveredCurrentPrice - underlyingPrice;

        const numberFormatOptions: NumberFormatOptions = {
            useGroupFormat: false,
            alwaysUseSign: true,
        };
        
        return `
            <div style="display: inline-block;">
                <b>
                    Strike price: 
                </b>
            </div>
            <div style="display: inline-block;">
                ${hoveredCurrentPrice}, ${numberFormatters.format0(deltaPrice, numberFormatOptions)}
            </div>
        `;
    }

    private getImpliedVolatilityRow(seriesCollection: ChartSeries[]): string {
        const numberFormatOptions: NumberFormatOptions = {
            useGroupFormat: false,
            alwaysUseSign: false,
        };
        
        const hoveredImpliedVolatility = seriesCollection[0].value[1];
        
        return `
            <div>
                <div style="display: inline-block;">
                    <b>
                        IV:
                    </b>
                </div>
                <div style="display: inline-block;">
                    ${numberFormatters.format1(hoveredImpliedVolatility, numberFormatOptions)}%
                </div>
            </div>
        `;
    }

    private getEstimatedImpliedVolatilityRow(
        chartData: number[][], seriesCollection: ChartSeries[],
        underlyingPrice: number, targetStrike: number
    ): string {
        const numberFormatOptions: NumberFormatOptions = {
            useGroupFormat: false,
            alwaysUseSign: false,
        };

        const hoveredCurrentPrice = seriesCollection[0].value[0];
        const deltaPrice = targetStrike - hoveredCurrentPrice;
        const targetPrice = underlyingPrice + deltaPrice;
        
        const estimatedImpliedVolatility = this.getImpliedVolatilityAtPrice(chartData, targetPrice);
        
        return `
            <div style="display: inline-block;">
                <b>
                    EIV for ${targetStrike}:
                </b>
            </div>
            <div style="display: inline-block;">
                ${numberFormatters.format1(estimatedImpliedVolatility, numberFormatOptions)}%
            </div>
        `;
    }
    
    private getImpliedVolatilityAtPrice = (
        chartData: number[][],
        price: number,
    ): number | null => {
        const strikes = chartData[0];
        const impliedVolatilityValues = chartData[1];

        for (let i = 0; i < strikes.length - 1; i++) {
            if (strikes[i] <= price && strikes[i + 1] > price ||
                strikes[i] >= price && strikes[i + 1] < price)
            {
                return impliedVolatilityValues[i];
            }
        }

        // If not enough IV values - calculate it using the latest available delta IV values

        // todo: BUG: IT WORKS FOR RIGHT SIDE, LEFT SIDE DOESNT WORK
        
        const step = strikes[strikes.length - 1] - strikes[strikes.length - 2];
        const deltaIV = impliedVolatilityValues[strikes.length - 1] - impliedVolatilityValues[strikes.length - 2]
        
        const latestStrike = strikes[strikes.length - 1];
        const latestImpliedVolatility = impliedVolatilityValues[strikes.length - 1];
        
        const remainingStrikeValueToTarget = price - latestStrike;
        const remainingStrikeValueToTargetInSteps = remainingStrikeValueToTarget / step;
        
        return latestImpliedVolatility + remainingStrikeValueToTargetInSteps * deltaIV;
    }
}

export const impliedVolatilityTooltipService = new ImpliedVolatilityTooltipService();